import {
	CircularProgress,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Skeleton,
	SxProps,
	Theme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InvoiceApi, {
	getFutureAndGeneratedInvoicesQueryKey,
	getUseInvoiceQueryKey,
} from '../../../api/InvoiceApi';
import {
	getPaginatedPaymentsQueryKey,
	getPaymentQueryKey,
	getPaymentsQueryKey,
} from '../../../api/PaymentsApi';
import { getUsePolicyQueryKey, usePolicy } from '../../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../../api/PolicyholderApi';
import { useAuthContext } from '../../../context/auth';
import {
	APPLY_PAYMENT_FAILURE,
	APPLY_PAYMENT_SUCCESS,
} from '../../../data/labelsAndMessages';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment } from '../../../types/payments';
import { InvoicePaymentRequest } from '../../../types/policy';
import { formatCurrency } from '../../../util/util';
import { errorToast, successToast } from '../../Toast';
import {
	APPLY_PAYMENT_MODAL__CANCEL_BUTTON,
	APPLY_PAYMENT_MODAL__DONE_BUTTON,
	APPLY_PAYMENT_MODAL__INVOICE,
	APPLY_PAYMENT_MODAL__NO_INVOICE,
	APPLY_PAYMENT_MODAL__NO_INVOICE_ERROR,
	APPLY_PAYMENT_MODAL__PAYMENT_AMOUNT,
	APPLY_PAYMENT_MODAL__PAYMENT_ID,
	APPLY_PAYMENT_MODAL__PAY_NOW_BUTTON,
	APPLY_PAYMENT_MODAL__POLICY,
	APPLY_PAYMENT_MODAL__POLICYHOLDER,
	APPLY_PAYMENT_MODAL__POLICY_NO_INVOICE,
	APPLY_PAYMENT_MODAL__SELECT_INVOICE,
	APPLY_PAYMENT_MODAL__TITLE,
	APPLY_PAYMENT_MODAL__TOTAL_DUE,
} from '../data/constants';

interface ApplyPaymentModalProps {
	open: boolean;
	setOpen: (val: Boolean) => void;
	payment: Payment;
	paymentsPageSize?: number;
}

interface ApplyPaymentModalForm {
	invoiceLocator: string;
}

const BORDER_STYLES: SxProps<Theme> = {
	border: 1,
	borderRadius: 2,
	borderColor: 'white.dark',
};

export default function ApplyPaymentModal(props: ApplyPaymentModalProps) {
	const authData = useAuthContext();
	const queryClient = useQueryClient();
	const { payment, paymentsPageSize } = props;
	const { open, setOpen } = props;
	const authToken = authData.auth?.authorizationToken;
	const { data: policy, isLoading: isPolicyLoading } = usePolicy(
		payment.policyLocator,
		authToken,
	);
	let outstandingInvoices =
		policy?.invoices?.filter(
			(invoice) =>
				invoice.settlementStatus === 'outstanding' &&
				invoice.totalDueCurrency === payment.amountCurrency,
		) || [];
	if (parseFloat(payment.amount) < 0) {
		// hide positive invoices for negative payment
		outstandingInvoices = outstandingInvoices.filter(
			(invoice) => parseFloat(invoice.totalDue) < 0,
		);
	} else if (parseFloat(payment.amount) > 0) {
		// hide negative invoices for positive payment
		outstandingInvoices = outstandingInvoices.filter(
			(invoice) => parseFloat(invoice.totalDue) > 0,
		);
	}
	const {
		handleSubmit,
		control,
		reset: resetForm,
		formState: { errors },
		setError,
	} = useForm<ApplyPaymentModalForm>({
		defaultValues: {
			invoiceLocator: '',
		},
	});

	const handleClose = () => {
		setOpen(false);
		resetForm();
	};

	const refetchData = async (invoiceLocator: string) => {
		queryClient.invalidateQueries(
			getPaymentQueryKey(payment.locator, authToken),
		);
		queryClient.invalidateQueries(
			getUsePolicyQueryKey(policy?.locator, authToken),
		);
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(policy?.locator, authToken),
		);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(
				authToken,
				policy?.policyholderLocator,
			),
		);
		queryClient.invalidateQueries(
			getPaymentsQueryKey(
				authToken,
				policy?.policyholderLocator,
				policy?.locator,
			),
		);
		queryClient.invalidateQueries(
			getUseInvoiceQueryKey(authToken, invoiceLocator),
		);
		if (typeof paymentsPageSize !== 'undefined') {
			queryClient.invalidateQueries(
				getPaginatedPaymentsQueryKey(
					paymentsPageSize,
					authToken,
					policy?.policyholderLocator,
					policy?.locator,
				),
			);
			queryClient.invalidateQueries(
				getPaginatedPaymentsQueryKey(
					paymentsPageSize,
					authToken,
					undefined,
					policy?.locator,
					undefined,
				),
			);
			queryClient.invalidateQueries(
				getPaginatedPaymentsQueryKey(
					paymentsPageSize,
					authToken,
					policy?.policyholderLocator,
					undefined,
					undefined,
				),
			);
			queryClient.invalidateQueries(
				getPaginatedPaymentsQueryKey(
					paymentsPageSize,
					authToken,
					undefined,
					undefined,
					undefined,
				),
			);
		}
	};

	const applyPayment = useMutation({
		mutationFn: (invoiceLocator: string) =>
			InvoiceApi.payInvoice(authToken, invoiceLocator, {
				paymentLocator: payment.locator,
			} as InvoicePaymentRequest),
		onSuccess: (data) => {
			refetchData(data?.data?.invoiceLocator);
			successToast(APPLY_PAYMENT_SUCCESS());
			handleClose();
		},
		onError: () => {
			errorToast(APPLY_PAYMENT_FAILURE());
		},
	});

	const onSubmit: SubmitHandler<ApplyPaymentModalForm> = (fieldValues) => {
		if (!fieldValues?.invoiceLocator) {
			setError('invoiceLocator', {
				message: APPLY_PAYMENT_MODAL__NO_INVOICE_ERROR,
			});
		} else {
			applyPayment.mutate(fieldValues.invoiceLocator);
		}
	};

	const getDialogContent = () => {
		return (
			<form
				onSubmit={(e) => {
					// Prevents the form from submitting any forms
					e.stopPropagation();
					handleSubmit(onSubmit)(e);
				}}
			>
				<DialogTitle>{APPLY_PAYMENT_MODAL__TITLE}</DialogTitle>
				<DialogContent>
					<Stack direction='row' paddingBottom={2}>
						<Stack direction='column' width='100%'>
							<Typography color='black.opacity60'>
								{APPLY_PAYMENT_MODAL__PAYMENT_ID}
							</Typography>
							<Typography variant='bodyMedium'>{payment.displayId}</Typography>
						</Stack>
						<Stack direction='column' width='100%'>
							<Typography color='black.opacity60'>
								{APPLY_PAYMENT_MODAL__PAYMENT_AMOUNT}
							</Typography>
							<Typography variant='bodyMedium'>
								{formatCurrency(
									parseFloat(payment.amount),
									payment.amountCurrency as CurrencyCode,
								)}
							</Typography>
						</Stack>
					</Stack>
					<Stack direction='row' paddingBottom={2}>
						<Stack direction='column' width='100%'>
							<Typography color='black.opacity60'>
								{APPLY_PAYMENT_MODAL__POLICYHOLDER}
							</Typography>
							<Typography variant='bodyMedium'>
								{payment.policyholderLocator}
							</Typography>
						</Stack>
						<Stack direction='column' width='100%'>
							<Typography color='black.opacity60'>
								{APPLY_PAYMENT_MODAL__POLICY}
							</Typography>
							<Typography variant='bodyMedium'>
								{payment.policyLocator}
							</Typography>
						</Stack>
					</Stack>
					<Stack direction='row' paddingBottom={2}>
						<Typography
							variant='bodySemiBold'
							id='ApplyPaymentModal__RadioButtonsGroupLabel'
						>
							{APPLY_PAYMENT_MODAL__SELECT_INVOICE}
						</Typography>
					</Stack>
					{isPolicyLoading ? (
						<>
							<Skeleton
								sx={{
									...BORDER_STYLES,
								}}
								height={80}
							/>
						</>
					) : (
						<Controller
							name='invoiceLocator'
							control={control}
							render={({ field }) => (
								<RadioGroup {...field}>
									{outstandingInvoices?.length ? (
										outstandingInvoices.map((invoice) => (
											<FormControlLabel
												key={invoice.displayId}
												value={invoice.locator}
												control={<Radio color='secondary' size='small' />}
												sx={{
													...BORDER_STYLES,
													borderColor:
														field.value === invoice.locator
															? 'secondary.main'
															: 'white.dark',
													marginX: 0,
													marginBottom: 2,

													'&:last-child': {
														marginBottom: 0,
													},

													'& .MuiFormControlLabel': {
														'&-label': {
															width: '100%',
															paddingX: 1,
														},
													},
												}}
												label={
													<Stack direction='row'>
														<Stack direction='column' width='100%'>
															<Typography color='black.opacity60'>
																{APPLY_PAYMENT_MODAL__INVOICE}
															</Typography>
															<Typography variant='bodyMedium'>
																{invoice.displayId}
															</Typography>
														</Stack>
														<Stack direction='column' width='100%'>
															<Typography color='black.opacity60'>
																{APPLY_PAYMENT_MODAL__TOTAL_DUE}
															</Typography>
															<Typography variant='bodyMedium'>
																{formatCurrency(
																	parseFloat(invoice.totalDue),
																	invoice.totalDueCurrency,
																)}
															</Typography>
														</Stack>
													</Stack>
												}
												labelPlacement='start'
											/>
										))
									) : (
										<Box
											sx={{
												...BORDER_STYLES,
												padding: 1,
											}}
										>
											<Typography>{APPLY_PAYMENT_MODAL__NO_INVOICE}</Typography>
											<Typography variant='caption' color='black.light'>
												{APPLY_PAYMENT_MODAL__POLICY_NO_INVOICE}
											</Typography>
										</Box>
									)}
								</RadioGroup>
							)}
						/>
					)}
					{errors?.invoiceLocator && (
						<FormHelperText error>
							{errors.invoiceLocator.message}
						</FormHelperText>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						marginRight: 2,
						marginY: 2,
						position: 'sticky',
						bottom: 0,
						backgroundColor: 'white.lightest',
					}}
				>
					{!isPolicyLoading && (
						<>
							{outstandingInvoices?.length ? (
								<>
									<Button onClick={handleClose} color='neutralButton'>
										{APPLY_PAYMENT_MODAL__CANCEL_BUTTON}
									</Button>
									<Button
										type='submit'
										variant='contained'
										color='secondary'
										disabled={applyPayment.isLoading}
										startIcon={
											applyPayment.isLoading && <CircularProgress size={20} />
										}
									>
										{APPLY_PAYMENT_MODAL__PAY_NOW_BUTTON}
									</Button>
								</>
							) : (
								<>
									{authData?.hasPermission('invoice.pay') ? (
										<Button
											type='submit'
											variant='contained'
											color='secondary'
											onClick={handleClose}
										>
											{APPLY_PAYMENT_MODAL__DONE_BUTTON}
										</Button>
									) : null}
								</>
							)}
						</>
					)}
				</DialogActions>
			</form>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} scroll='paper'>
			<Box
				sx={{
					width: {
						xs: '90%',
						sm: '90%',
						md: 600,
						lg: 600,
						xl: 600,
					},
					overflowX: 'hidden',
				}}
			>
				{getDialogContent()}
			</Box>
		</Dialog>
	);
}
